<template>
  <div class="home">
    <AppHeader @locale-change="getNews" @scrolled="showTop = $event"/>

    <!-- Početni banner -->
    <section class="hero" aria-labelledby="hero-title">
      <h1 id="hero-title" class="pr-sr-only">
        PR, Media &amp; Sports Days University of Mostar – {{ $t('conf.theme') }}, Mostar, {{ $t('conf.date') }}
      </h1>
      <img
          class="hero__img"
          :src="require('@/assets/2026/banner_2026.png')"
          width="1920"
          height="1080"
          :alt="'PR, Media & Sports Days – ' + $t('conf.theme') + ', Mostar, ' + $t('conf.date')"
      />
      <div class="hero__caption">
        <div class="pr-container">
          <span class="pr-eyebrow">{{ $t('conf.eyebrow') }}</span>
          <p class="hero__caption-theme">{{ $t('conf.theme') }}</p>
          <p class="hero__caption-date">
            <v-icon small color="#ed1c24">mdi-calendar-blank-outline</v-icon>
            Mostar, {{ $t('conf.date') }}
          </p>
        </div>
      </div>
    </section>

    <!-- Datum, kalendar i odbrojavanje -->
    <section id="program" class="pr-section conf">
      <div class="pr-container">
        <div v-if="event.livestreamUrl" class="conf-live">
          <iframe
              :src="event.livestreamUrl"
              title="Livestream"
              frameborder="0"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share"
              referrerpolicy="strict-origin-when-cross-origin"
              allowfullscreen
          ></iframe>
        </div>

        <div class="conf-panel">
          <div class="conf-panel__main">
            <span class="pr-eyebrow">{{ $t('conf.eyebrow') }}</span>
            <p class="conf-panel__type">{{ $t('conf.conference_type') }}</p>
            <p class="conf-panel__date">{{ $t('conf.date') }}</p>
            <p class="conf-panel__theme-label">{{ $t('conf.theme_label') }}</p>
            <h2 class="conf-panel__theme">{{ $t('conf.theme') }}</h2>
            <div class="conf-panel__actions">
              <button type="button" class="pr-btn pr-btn--red" @click="addToGoogleCalendar">
                <v-icon>mdi-google</v-icon>
                {{ $t('conf.calendar_google') }}
              </button>
            </div>
            <a v-if="event.programUrl" :href="event.programUrl" target="_blank" rel="noopener" class="conf-panel__program">
              <v-icon small>mdi-download</v-icon>
              {{ $t('conf.program_download') }}
            </a>
            <p v-else class="conf-panel__program conf-panel__program--soon">
              <v-icon small>mdi-information-outline</v-icon>
              {{ $t('conf.program_soon') }}
            </p>
          </div>
          <div class="conf-panel__side">
            <p class="conf-panel__count-title">{{ $t('conf.countdown_title') }}</p>
            <ConferenceCountdown
                :start="event.start"
                :end="event.end"
                :labels="$t('conf.countdown_units')"
                :live-text="$t('conf.countdown_live')"
                :done-text="$t('conf.countdown_done')"
            />
            <p class="conf-panel__hashtag">{{ $t('start') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Poziv za sudjelovanje, rokovi i informacije -->
    <section id="poziv" class="pr-section pr-section--white call">
      <div class="pr-container">
        <div class="call__intro">
          <div>
            <span class="pr-eyebrow">{{ $t('conf.call_title') }}</span>
            <h2 class="pr-title">PR 4.0</h2>
            <p class="call__theme">{{ $t('conf.theme_short') }}</p>
            <p class="pr-lead">{{ $t('conf.intro') }}</p>
            <p class="pr-lead">{{ $t('conf.focus') }}</p>
            <div class="call__actions">
              <a class="pr-btn pr-btn--primary" :href="'mailto:' + event.email">
                <v-icon>mdi-send-outline</v-icon>
                {{ $t('conf.send_abstract') }}
              </a>
              <a class="pr-btn pr-btn--ghost" :href="event.callPdf" target="_blank" rel="noopener">
                <v-icon>mdi-file-pdf-box</v-icon>
                {{ $t('conf.call_download') }}
              </a>
            </div>
          </div>

          <div class="dates pr-card">
            <h3 class="pr-subtitle">{{ $t('conf.dates_title') }}</h3>
            <div v-for="group in $t('conf.dates_groups')" :key="group.title" class="dates__group">
              <p class="dates__group-title">{{ group.title }}</p>
              <ol class="dates__list">
                <li
                    v-for="item in group.items"
                    :key="item.label"
                    class="dates__item"
                    :class="{ 'dates__item--highlight': item.highlight }"
                >
                  <span class="dates__date">{{ item.date }}</span>
                  <span class="dates__label">{{ item.label }}</span>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <h3 class="pr-subtitle call__facts-title">{{ $t('conf.facts_title') }}</h3>
        <div class="facts">
          <div v-for="fact in $t('conf.facts')" :key="fact.label" class="fact">
            <v-icon class="fact__icon">{{ fact.icon }}</v-icon>
            <div>
              <p class="fact__label">{{ fact.label }}</p>
              <a v-if="fact.href" :href="fact.href" class="fact__value" target="_blank" rel="noopener">{{ fact.value }}</a>
              <p v-else class="fact__value">{{ fact.value }}</p>
            </div>
          </div>
          <div class="fact fact--wide">
            <v-icon class="fact__icon">mdi-view-grid-outline</v-icon>
            <div>
              <p class="fact__label">{{ $t('conf.sections_label') }}</p>
              <div class="pr-chips">
                <span v-for="s in $t('conf.sections')" :key="s" class="pr-chip">{{ s }}</span>
              </div>
            </div>
          </div>
          <div class="fact fact--wide">
            <v-icon class="fact__icon">mdi-database-check-outline</v-icon>
            <div>
              <p class="fact__label">{{ $t('conf.indexing_label') }}</p>
              <div class="pr-chips">
                <span v-for="s in $t('conf.indexing')" :key="s" class="pr-chip">{{ s }}</span>
              </div>
            </div>
          </div>
        </div>

        <v-expansion-panels v-model="openPanels" multiple flat class="call__panels">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="call__panel-title"><v-icon>mdi-lightbulb-on-outline</v-icon>{{ $t('conf.topics_title') }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ol class="topics">
                <li v-for="topic in $t('conf.topics')" :key="topic">{{ topic }}</li>
              </ol>
              <p class="call__note">{{ $t('conf.topics_note') }}</p>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="call__panel-title"><v-icon>mdi-text-box-edit-outline</v-icon>{{ $t('conf.abstract_title') }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="abstract">
                <ul class="checklist">
                  <li v-for="line in $t('conf.abstract_format')" :key="line">{{ line }}</li>
                </ul>
                <div>
                  <p class="abstract__label">{{ $t('conf.abstract_contents_label') }}</p>
                  <ul class="checklist">
                    <li v-for="line in $t('conf.abstract_contents')" :key="line">{{ line }}</li>
                  </ul>
                </div>
              </div>
              <p class="call__note">
                {{ $t('conf.abstract_send') }}
                <a :href="'mailto:' + event.email">{{ event.email }}</a>
              </p>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="call__panel-title"><v-icon>mdi-book-open-page-variant-outline</v-icon>{{ $t('conf.publication_title') }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <p class="call__text">{{ $t('conf.publication_text') }}</p>
              <div class="pr-chips mb-4">
                <span v-for="s in $t('conf.indexing')" :key="s" class="pr-chip">{{ s }}</span>
              </div>
              <a :href="event.journalUrl" target="_blank" rel="noopener" class="call__link">
                {{ $t('conf.publication_link') }} – seejc.sum.ba
                <v-icon small color="#1553d1">mdi-arrow-top-right</v-icon>
              </a>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </section>

    <!-- Novosti -->
    <section id="novosti" class="pr-section">
      <div class="pr-container">
        <div class="pr-section-head">
          <div>
            <span class="pr-eyebrow">PR, Media &amp; Sports Days</span>
            <h2 class="pr-title">{{ $t('novosti') }}</h2>
          </div>
        </div>
        <div class="post-grid">
          <router-link
              v-for="item in news"
              :key="item.id || item.alias"
              :to="'/novosti/' + item.alias"
              class="pr-post-card"
          >
            <div class="pr-post-card__media">
              <img
                  v-if="item.image"
                  class="pr-post-card__img"
                  :src="storageUrl(item.image)"
                  :alt="item.title"
                  loading="lazy"
              />
            </div>
            <div class="pr-post-card__body">
              <span class="pr-post-card__tag">{{ $t('novosti') }}</span>
              <h3 class="pr-post-card__title">{{ item.title }}</h3>
              <p class="pr-post-card__excerpt">{{ excerpt(item.content) }}</p>
            </div>
          </router-link>
        </div>
        <div class="section-more">
          <button type="button" class="pr-btn pr-btn--ghost" @click="getOtherNews">
            {{ $t('more_news') }}
            <v-icon>mdi-chevron-down</v-icon>
          </button>
        </div>
      </div>
    </section>

    <!-- O konferenciji -->
    <section id="konferencija" class="pr-section pr-section--navy about">
      <div class="pr-container about__grid">
        <div>
          <span class="pr-eyebrow">{{ $t('conf.eyebrow') }}</span>
          <h2 class="pr-title">{{ $t('about_title') }}</h2>
        </div>
        <p class="about__text">{{ $t('about_2023') }}</p>
      </div>
    </section>

    <!-- Dosadašnja izdanja -->
    <section id="arhiva" class="pr-section">
      <div class="pr-container">
        <div class="pr-section-head">
          <div>
            <span class="pr-eyebrow">{{ $t('vise') }}</span>
            <h2 class="pr-title">{{ $t('conf.editions_title') }}</h2>
          </div>
        </div>
        <div class="editions">
          <article v-for="edition in editions" :key="edition.key" class="edition pr-card">
            <div class="edition__head">
              <span class="edition__num">{{ edition.num }}</span>
              <span class="edition__year">{{ edition.year }}</span>
            </div>
            <h3 class="edition__title">{{ $t(edition.key) }}</h3>
            <p
                class="edition__text"
                :class="{ 'edition__text--open': expandedEditions.indexOf(edition.key) !== -1 }"
                v-html="$t(edition.key + '_text')"
            ></p>
            <div class="edition__actions">
              <button
                  type="button"
                  class="edition__toggle"
                  :aria-expanded="expandedEditions.indexOf(edition.key) !== -1 ? 'true' : 'false'"
                  @click="toggleEdition(edition.key)"
              >
                {{ expandedEditions.indexOf(edition.key) !== -1 ? $t('conf.read_less') : $t('conf.read_more') }}
                <v-icon small>{{ expandedEditions.indexOf(edition.key) !== -1 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </button>
              <router-link v-if="edition.archive" :to="'/arhiva/' + edition.year" class="edition__link">
                {{ $t('vise') }} {{ edition.year }}
                <v-icon small>mdi-arrow-right</v-icon>
              </router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Galerija -->
    <section id="galerija" class="pr-section pr-section--white gallery">
      <div class="pr-container">
        <div class="pr-section-head">
          <div>
            <span class="pr-eyebrow">PR, Media &amp; Sports Days</span>
            <h2 class="pr-title">{{ $t('conf.gallery') }}</h2>
          </div>
        </div>
        <carousel-3d
            autoplay
            :autoplay-timeout="4000"
            :width="480"
            :height="320"
            :border="0"
            :space="340"
            :display="5"
            :controls-visible="true"
            class="gallery__carousel"
        >
          <slide v-for="(slide, i) in slides" :index="i" :key="i">
            <img :src="slide.src" alt="PR, Media & Sports Days – galerija" loading="lazy"/>
          </slide>
        </carousel-3d>
      </div>
    </section>

    <!-- Organizacijski odbor i tajnik -->
    <section id="organizacijski_odbor" class="pr-section committee">
      <div class="pr-container">
        <div class="pr-section-head">
          <div>
            <span class="pr-eyebrow">PR, Media &amp; Sports Days</span>
            <h2 class="pr-title">{{ $t('conf.committee_title') }}</h2>
          </div>
        </div>

        <div class="committee-grid">
          <article v-for="member in committee" :key="member.name" class="member">
            <h3 class="member__name">{{ member.name }}</h3>
            <p class="member__inst">({{ $t('conf.institutions.' + member.inst) }})</p>
            <p class="member__role">{{ $t('conf.roles.' + member.role) }}</p>
          </article>
        </div>

        <div class="secretary">
          <div class="secretary__head">
            <span class="pr-eyebrow">PR, Media &amp; Sports Days</span>
            <h2 class="pr-title">{{ $t('conf.secretary_title') }}</h2>
          </div>
          <article class="member member--secretary">
            <span class="member__avatar">
              <v-icon>mdi-account-outline</v-icon>
            </span>
            <div>
              <h3 class="member__name">{{ secretary.name }}</h3>
              <p class="member__inst">({{ $t('conf.institutions.' + secretary.inst) }})</p>
              <p class="member__role">{{ $t('conf.roles.' + secretary.role) }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Sponzori i medijski partneri -->
    <section class="pr-section pr-section--white partners">
      <div class="pr-container">
        <h2 class="partners__title">{{ $t('conf.sponsors') }}</h2>
        <div class="logo-grid">
          <div v-for="s in sponsors" :key="s.name" class="logo-tile">
            <img :src="s.img" :alt="s.name" loading="lazy"/>
          </div>
        </div>

        <h2 class="partners__title">{{ $t('conf.media_partners') }}</h2>
        <div class="logo-grid">
          <component
              :is="m.href ? 'a' : 'div'"
              v-for="m in mediaPartners"
              :key="m.name"
              :href="m.href"
              :target="m.href ? '_blank' : null"
              :rel="m.href ? 'noopener' : null"
              class="logo-tile"
              :aria-label="m.href ? m.name : null"
          >
            <img :src="m.img" :alt="m.name" loading="lazy"/>
          </component>
        </div>
      </div>
    </section>

    <!-- Kontakt -->
    <section id="kontakt" class="pr-section contact">
      <div class="pr-container contact__grid">
        <div class="contact__card pr-card">
          <span class="pr-eyebrow">{{ $t('contact') }}</span>
          <h2 class="pr-title">PR, Media &amp; Sports Days</h2>
          <ul class="contact__list">
            <li>
              <v-icon>mdi-map-marker-outline</v-icon>
              <div>
                <span class="contact__label">{{ $t('conf.address_label') }}</span>
                {{ $t('conf.address') }}
              </div>
            </li>
            <li>
              <v-icon>mdi-phone-outline</v-icon>
              <div>
                <span class="contact__label">{{ $t('conf.phone_label') }}</span>
                <a :href="'tel:' + event.phone.replace(/[^+\d]/g, '').replace('+3870', '+387')">{{ event.phone }}</a>
              </div>
            </li>
            <li>
              <v-icon>mdi-email-outline</v-icon>
              <div>
                <span class="contact__label">{{ $t('conf.email_label') }}</span>
                <a :href="'mailto:' + event.email">{{ event.email }}</a>
              </div>
            </li>
            <li>
              <v-icon>mdi-web</v-icon>
              <div>
                <span class="contact__label">{{ $t('conf.web_label') }}</span>
                <a :href="event.web">prdays.sum.ba</a>
              </div>
            </li>
          </ul>
        </div>
        <div class="contact__card contact__card--social">
          <h3 class="pr-subtitle">{{ $t('social') }}</h3>
          <div class="socials">
            <a v-for="s in socials" :key="s.name" :href="s.href" target="_blank" rel="noopener" class="social" :style="{ '--brand': s.color }" :aria-label="s.name">
              <v-icon>{{ s.icon }}</v-icon>
            </a>
          </div>
          <a class="pr-btn pr-btn--light mt-8" :href="'mailto:' + event.email">
            <v-icon>mdi-send-outline</v-icon>
            {{ $t('conf.send_abstract') }}
          </a>
        </div>
      </div>
    </section>

    <v-fade-transition>
      <button v-if="showTop" type="button" class="to-top" :aria-label="$t('conf.back_to_top')" @click="scrollTop">
        <v-icon color="white">mdi-chevron-up</v-icon>
      </button>
    </v-fade-transition>
  </div>
</template>

<script>
import axios from 'axios'
import AppHeader from './AppHeader'
import ConferenceCountdown from './ConferenceCountdown'
import {EVENT} from '@/plugins/conf2026'

const STORAGE = 'https://web-admin.sum.ba/api/storage/'

export default {
  name: 'Home',
  components: {AppHeader, ConferenceCountdown},
  created() {
    localStorage.setItem("language", this.$i18n.locale)
    this.getNews()
  },

  methods: {
    getNews() {
      const requestBody = {
        postsPerPage: this.numberNews,
        alias: ["prdays"],
      };
      axios
          .post("https://web-admin.sum.ba/api/web/objave", requestBody)
          .then(response => {
            this.news = response.data;
          })
    },
    getOtherNews() {
      this.numberNews += 3;
      this.getNews();
    },
    storageUrl(path) {
      return STORAGE + path
    },
    excerpt(html, length = 180) {
      if (!html) return ''
      const text = new DOMParser().parseFromString(html, 'text/html').body.textContent.replace(/\s+/g, ' ').trim()
      return text.length > length ? text.slice(0, length).replace(/\s+\S*$/, '') + '…' : text
    },
    scrollTop() {
      window.scrollTo({top: 0, behavior: 'smooth'})
    },
    addToGoogleCalendar() {
      const params = [
        'action=TEMPLATE',
        'text=' + encodeURIComponent(this.event.name),
        'dates=' + this.event.calendarStart + '/' + this.event.calendarEnd,
        'details=' + encodeURIComponent(this.$t('conf.calendar_details')),
        'location=' + encodeURIComponent(this.event.location),
      ]
      window.open('https://calendar.google.com/calendar/render?' + params.join('&'), '_blank', 'noopener')
    },
    toggleEdition(key) {
      const i = this.expandedEditions.indexOf(key)
      if (i === -1) this.expandedEditions.push(key)
      else this.expandedEditions.splice(i, 1)
    },
  },

  data: () => ({
    event: EVENT,
    numberNews: 3,
    news: [],
    showTop: false,
    openPanels: [],
    expandedEditions: [],
    editions: [
      {num: '10', key: 'tenth', year: '2025', archive: true},
      {num: '09', key: 'ninth', year: '2024', archive: true},
      {num: '08', key: 'eighth', year: '2023', archive: true},
      {num: '07', key: 'seventh', year: '2022', archive: true},
      {num: '06', key: 'sixth', year: '2021', archive: true},
      {num: '05', key: 'fifth', year: '2020', archive: true},
      {num: '04', key: 'fourth', year: '2019', archive: true},
      {num: '03', key: 'third', year: '2018'},
      {num: '02', key: 'second', year: '2017'},
      {num: '01', key: 'first', year: '2016'},
    ],
    socials: [
      {name: 'Facebook', icon: 'mdi-facebook', color: '#1877f2', href: 'https://www.facebook.com/prdaysmostariensis/'},
      {name: 'Instagram', icon: 'mdi-instagram', color: '#e1306c', href: 'https://instagram.com/pr.media.sport.days?igshid=YmMyMTA2M2Y='},
      {name: 'YouTube', icon: 'mdi-youtube', color: '#ff0000', href: 'https://youtube.com/channel/UCBIJsHAH1FjJnruAx_oDsdw'},
      {name: 'LinkedIn', icon: 'mdi-linkedin', color: '#0a66c2', href: 'https://www.linkedin.com/in/pr-media-and-sport-days-mostariensis-67240a152'},
      {name: 'X / Twitter', icon: 'mdi-twitter', color: '#1d9bf0', href: 'https://twitter.com/prmostariensis?t=EH5wGvFFW2g5vgZeRQVeQg&s=09'},
    ],
    // Sponzori 2026. — redoslijed prema uputama Organizacijskog odbora
    sponsors: [
      {name: 'Vlada Županije Zapadnohercegovačke', img: require('@/assets/2026/vlada-zzh.png')},
      {name: 'HT Eronet', img: require('@/assets/2024/eronet.png')},
      {name: 'Config', img: require('@/assets/2025/config.png')},
      {name: 'Kopirnica Zrinjski', img: require('@/assets/2026/zrinjski-kopirnica.png')},
    ],
    // Medijski partneri 2026. — redoslijed prema uputama Organizacijskog odbora
    mediaPartners: [
      {name: 'BHRT', href: 'https://www.bhrt.ba/', img: require('@/assets/2026/bhrt.png')},
      {name: 'FENA', href: 'https://fena.ba/', img: require('@/assets/img/fena.png')},
      {name: 'Večernji list', href: 'https://www.vecernji.ba/', img: require('@/assets/img/vecernji.png')},
      {name: 'Običan radio', href: 'https://www.obican.info/', img: require('@/assets/2024/obican-radio.png')},
      {name: 'Radio Čapljina', img: require('@/assets/2026/radio-capljina.png')},
      {name: 'Dalmacija News', href: 'https://www.dalmacijanews.hr/', img: require('@/assets/img/dalmacijaNews.png')},
      {name: 'Mostarska panorama', href: 'https://www.glasmostara.ba/', img: require('@/assets/img/panorama.png')},
      {name: 'TV Jadran', href: 'https://tvjadran.hr/', img: require('@/assets/2026/tv-jadran.png')},
      {name: 'Artinfo.ba', href: 'https://www.artinfo.ba/', img: require('@/assets/2026/artinfo.png')},
      {name: 'Manager.ba', href: 'https://manager.ba/', img: require('@/assets/2026/manager-ba.png')},
      {name: 'Pogled.ba', href: 'https://pogled.ba/', img: require('@/assets/img/Slika15.png')},
      {name: 'SUM Sova', href: 'https://sumsova.ba/', img: require('@/assets/2026/sum-sova.png')},
      {name: 'SUM TV', href: 'https://tv.sum.ba/', img: require('@/assets/img/sum-tv-blue.svg')},
    ],
    slides: [
      {src: require("@/assets/gallery/slika1_2022.png")},
      {src: require("@/assets/gallery/slika2_2022.png")},
      {src: require("@/assets/gallery/slika3_2022.png")},
      {src: require("@/assets/gallery/slika4_2022.png")},
      {src: require("@/assets/gallery/slika5_2022.png")},
      {src: require("@/assets/gallery/slika6_2022.png")},
      {src: require("@/assets/gallery/slika7_2022.png")},
      {src: require("@/assets/gallery/slika8_2022.png")},
      {src: require("@/assets/gallery/Slika1_2021.png")},
      {src: require("@/assets/gallery/Slika2_2021.png")},
      {src: require("@/assets/gallery/Slika3_2021.png")},
      {src: require("@/assets/gallery/Slika4_2021.png")},
      {src: require("@/assets/gallery/Slika5_2021.png")},
      {src: require("@/assets/gallery/Slika6_2021.png")},
      {src: require("@/assets/gallery/Slika7_2021.png")},
      {src: require("@/assets/img/slika1.jpg")},
      {src: require("@/assets/img/slika2.jpg")},
      {src: require("@/assets/img/slika3.jpg")},
      {src: require("@/assets/img/slika4.jpg")},
      {src: require("@/assets/img/slika5.jpg")},
      {src: require("@/assets/img/slika6.jpg")},
      {src: require("@/assets/img/slika7.jpg")},
      {src: require("@/assets/img/slika8.jpg")},
      {src: require("@/assets/img/slika9.jpg")},
      {src: require("@/assets/img/slika10.jpg")},
    ],
    // Organizacijski odbor 2026. — nazivi ustanova i uloge prevode se preko conf2026.js
    committee: [
      {name: "prof. dr. sc. Zoran Tomić", inst: "mostar", role: "founder"},
      {name: "doc. dr. sc. Marijan Primorac", inst: "mostar", role: "president"},
      {name: "prof. dr. sc. Dražen Barbarić", inst: "mostar", role: "member"},
      {name: "prof. dr. sc. Sanja Bijakšić", inst: "mostar", role: "member"},
      {name: "prof. dr. sc. Tomislav Volarić", inst: "mostar", role: "member"},
      {name: "prof. dr. sc. Zdenko Klepić", inst: "mostar", role: "member"},
      {name: "prof. dr. sc. Franjo Takač", inst: "mostar", role: "member"},
      {name: "doc. dr. sc. Damir Vasilj", inst: "mostar", role: "member"},
      {name: "prof. dr. sc. Nino Ćorić", inst: "mostar", role: "member"},
      {name: "prof. dr. sc. Miroslav Vasilj", inst: "mostar", role: "member"},
      {name: "doc. dr. sc. Đorđe Obradović", inst: "dubrovnik", role: "member"},
      {name: "dipl. iur. Davor Radić", inst: "fifa", role: "member"},
      {name: "MSc Ivan Tomić", inst: "sjever_alma", role: "member"},
      {name: "prof. dr. sc. Miro Radalj", inst: "zagreb_split", role: "member"},
      {name: "prof. dr. sc. Iva Rosanda Žigo", inst: "sjever", role: "member"},
      {name: "prof. dr. sc. Marin Milković", inst: "sjever", role: "member"},
      {name: "prof. dr. sc. Mario Tomiša", inst: "sjever", role: "member"},
      {name: "prof. dr. sc. Robert Geček", inst: "sjever", role: "member"},
      {name: "prof. dr. sc. Tamara Vlastelica", inst: "beograd", role: "member"},
      {name: "dr. sc. Antonia Tomaš", inst: "sjever", role: "member"},
      {name: "dr. sc. Moti Gigi", inst: "sapir", role: "member"},
      {name: "dr. sc. Ella Ben-Atar", inst: "sapir", role: "member"},
      {name: "dr. sc. Omer Keynan", inst: "sapir", role: "member"},
    ],
    secretary: {name: "Ivan Torbica, mag. inf.", inst: "mostar", role: "secretary"},
  })
};
</script>

<style lang="scss" scoped>
/* Hero */
.hero {
  background: linear-gradient(90deg, #0b2f7d 0%, #0b2f7d 45%, #eef1f6 55%, #eef1f6 100%);
}

.hero__img {
  display: block;
  width: 100%;
  max-width: 1920px;
  height: auto;
  margin: 0 auto;
}

.hero__caption {
  display: none;
  padding: 20px 0 22px;
  background: var(--pr-navy);
  color: #fff;

  .pr-eyebrow {
    color: #9cc0ff;
    margin-bottom: 6px;
  }
}

.hero__caption-theme {
  margin: 0 0 8px;
  font-family: var(--font-display);
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.05;
  text-transform: uppercase;
}

.hero__caption-date {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-weight: 600;
  color: rgba(255, 255, 255, .85);
}

@media (max-width: 768px) {
  .hero__caption {
    display: block;
  }
}

/* Konferencijski blok */
.conf {
  padding-top: 56px;
  padding-bottom: 56px;
}

.conf-live {
  position: relative;
  margin-bottom: 32px;
  overflow: hidden;
  border-radius: var(--pr-radius);
  background: #000;
  aspect-ratio: 16 / 9;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
}

.conf-panel {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: 48px;
  padding: 48px;
  border-radius: 24px;
  background: radial-gradient(120% 140% at 100% 0%, #1553d1 0%, rgba(21, 83, 209, 0) 55%),
  linear-gradient(135deg, #002652 0%, #06336f 100%);
  color: #fff;
  box-shadow: 0 24px 60px rgba(0, 38, 82, .25);

  .pr-eyebrow {
    color: #9cc0ff;
  }
}

.conf-panel__type {
  margin: 0 0 6px;
  font-size: 15px;
  color: rgba(255, 255, 255, .75);
}

.conf-panel__date {
  margin: 0 0 22px;
  font-family: var(--font-display);
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: .01em;
}

.conf-panel__theme-label {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, .6);
}

.conf-panel__theme {
  margin: 0 0 28px;
  font-size: clamp(1.15rem, 2vw, 1.4rem);
  font-weight: 600;
  line-height: 1.35;
}

.conf-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.conf-panel__program {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 22px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #fff !important;
  text-decoration: none;

  .v-icon {
    color: inherit !important;
  }
}

.conf-panel__program--soon {
  font-weight: 500;
  color: rgba(255, 255, 255, .7) !important;
}

.conf-panel__side {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 48px;
  border-left: 1px solid rgba(255, 255, 255, .14);
}

.conf-panel__count-title {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, .7);
}

.conf-panel__hashtag {
  margin: 20px 0 0;
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: .02em;
  color: #ff6b70;
}

@media (max-width: 960px) {
  .conf-panel {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 36px 28px;
  }
  .conf-panel__side {
    padding: 32px 0 0;
    border-left: 0;
    border-top: 1px solid rgba(255, 255, 255, .14);
  }
}

@media (max-width: 600px) {
  .conf {
    padding-top: 24px;
    padding-bottom: 40px;
  }
  .conf-panel {
    padding: 28px 20px;
    border-radius: 18px;
  }
  .conf-panel__actions .pr-btn {
    width: 100%;
  }
}

/* Poziv za sudjelovanje */
.call__intro {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 56px;
  align-items: start;
}

.call__theme {
  margin: 6px 0 20px;
  font-family: var(--font-display);
  font-size: clamp(1.4rem, 2.6vw, 1.9rem);
  font-weight: 700;
  line-height: 1.1;
  text-transform: uppercase;
  color: var(--pr-blue);
}

.call__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}

.dates {
  padding: 32px;
}

.dates__group + .dates__group {
  margin-top: 24px;
}

.dates__group-title {
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: var(--pr-muted);
}

.dates__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.dates__item {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0 0 16px 26px;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 5px;
    width: 12px;
    height: 12px;
    border: 2px solid var(--pr-blue);
    border-radius: 50%;
    background: #fff;
  }

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 20px;
    bottom: 2px;
    width: 2px;
    background: var(--pr-line);
  }

  &:last-child {
    padding-bottom: 0;
  }
}

.dates__item--highlight::before {
  border-color: var(--pr-red);
  background: var(--pr-red);
}

.dates__date {
  font-weight: 700;
  color: var(--pr-navy);
}

.dates__label {
  font-size: 14px;
  color: var(--pr-muted);
}

.call__facts-title {
  margin-top: 64px;
  margin-bottom: 20px;
}

.facts {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
}

.fact {
  display: flex;
  gap: 16px;
  padding: 22px;
  border: 1px solid var(--pr-line);
  border-radius: var(--pr-radius);
  background: var(--pr-bg);

  p {
    margin: 0;
  }
}

.fact {
  grid-column: span 2;
}

.fact--wide {
  grid-column: span 3;
}

.fact__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fff;
  color: var(--pr-blue) !important;
  box-shadow: inset 0 0 0 1px var(--pr-line);
}

.fact__label {
  margin-bottom: 4px !important;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--pr-muted);
}

.fact__value {
  font-weight: 600;
  line-height: 1.45;
  color: var(--pr-navy) !important;
  text-decoration: none;
  word-break: break-word;
}

a.fact__value:hover {
  color: var(--pr-blue) !important;
  text-decoration: underline;
}

.call__panels {
  margin-top: 40px;

  ::v-deep .v-expansion-panel {
    border: 1px solid var(--pr-line);
    border-radius: var(--pr-radius) !important;
    margin-top: 12px;

    &::before {
      box-shadow: none;
    }

    &::after {
      display: none;
    }
  }

  ::v-deep .v-expansion-panel-header {
    min-height: 68px;
    padding: 18px 24px;
  }

  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 24px 24px;
  }
}

.call__panel-title {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--pr-navy);

  .v-icon {
    color: var(--pr-blue) !important;
  }
}

.topics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 32px;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: topic;

  li {
    position: relative;
    padding-left: 38px;
    line-height: 1.5;
    color: #2b3445;
    counter-increment: topic;

    &::before {
      content: counter(topic, decimal-leading-zero);
      position: absolute;
      left: 0;
      top: 0;
      font-family: var(--font-display);
      font-weight: 700;
      color: var(--pr-blue);
    }
  }
}

.abstract {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 40px;
}

.abstract__label {
  margin: 0 0 8px;
  font-weight: 700;
  color: var(--pr-navy);
}

.checklist {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    padding: 4px 0 4px 28px;
    line-height: 1.5;
    color: #2b3445;

    &::before {
      content: "";
      position: absolute;
      left: 4px;
      top: 10px;
      width: 12px;
      height: 7px;
      border-left: 2px solid var(--pr-blue);
      border-bottom: 2px solid var(--pr-blue);
      transform: rotate(-45deg);
    }
  }
}

.call__note,
.call__text {
  margin: 20px 0 0;
  line-height: 1.7;
  color: var(--pr-muted);
}

.call__text {
  margin: 0 0 16px;
}

.call__link {
  font-weight: 600;
  text-decoration: none;
}

@media (max-width: 960px) {
  .call__intro {
    grid-template-columns: 1fr;
    gap: 36px;
  }
  .facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .fact {
    grid-column: span 1;
  }
  .fact--wide {
    grid-column: span 2;
  }
}

@media (max-width: 600px) {
  .dates {
    padding: 24px 20px;
  }
  .facts,
  .topics,
  .abstract {
    grid-template-columns: 1fr;
  }
  .fact--wide {
    grid-column: span 1;
  }
  .call__facts-title {
    margin-top: 44px;
  }
  .call__actions .pr-btn {
    width: 100%;
  }
  .call__panels {
    ::v-deep .v-expansion-panel-header {
      padding: 16px 18px;
    }
    ::v-deep .v-expansion-panel-content__wrap {
      padding: 0 18px 20px;
    }
  }
}

/* Novosti */
.post-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.section-more {
  display: flex;
  justify-content: center;
  margin-top: 36px;
}

@media (max-width: 960px) {
  .post-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .post-grid {
    grid-template-columns: 1fr;
  }
}

/* O konferenciji */
.about__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: 48px;
}

.about__text {
  margin: 0;
  font-size: 1.075rem;
  line-height: 1.85;
  color: rgba(255, 255, 255, .85);
}

@media (max-width: 960px) {
  .about__grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

/* Izdanja */
.editions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.edition {
  display: flex;
  flex-direction: column;
  padding: 26px;
  box-shadow: none;
  transition: box-shadow .25s ease, transform .25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--pr-shadow);
  }
}

.edition__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
}

.edition__num {
  font-family: var(--font-display);
  font-size: 2.6rem;
  font-weight: 800;
  line-height: 1;
  color: var(--pr-blue);
}

.edition__year {
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--pr-bg);
  font-size: 13px;
  font-weight: 700;
  color: var(--pr-navy);
}

.edition__title {
  margin: 0 0 10px;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--pr-navy);
}

.edition__text {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0 0 16px;
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--pr-muted);
}

.edition__text--open {
  display: block;
  -webkit-line-clamp: unset;
  overflow: visible;
}

.edition__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-top: auto;
}

.edition__toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  font-weight: 600;
  color: var(--pr-navy);

  .v-icon {
    color: inherit !important;
  }

  &:hover {
    color: var(--pr-blue);
  }
}

.edition__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  text-decoration: none;

  .v-icon {
    color: inherit !important;
    transition: transform .2s ease;
  }

  &:hover .v-icon {
    transform: translateX(3px);
  }
}

@media (max-width: 960px) {
  .editions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .editions {
    grid-template-columns: 1fr;
  }
}

/* Galerija */
.gallery__carousel {
  margin: 0 !important;

  ::v-deep .carousel-3d-slide {
    border-radius: 14px;
    background: var(--pr-bg) !important;

    img {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover;
    }
  }

  ::v-deep .carousel-3d-controls a {
    color: var(--pr-navy);
  }
}

/* Organizacijski odbor */
.committee-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.member {
  padding: 20px 22px;
  border: 1px solid var(--pr-line);
  border-radius: var(--pr-radius);
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 27, 45, .04);

  p {
    margin: 0;
  }
}

.member__name {
  margin: 0 0 4px;
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--pr-blue);
}

.member__inst {
  font-size: 14px;
  line-height: 1.45;
  color: var(--pr-muted);
}

.member__role {
  margin-top: 2px !important;
  font-size: 14px;
  color: var(--pr-navy);
}

.secretary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: 32px;
  align-items: center;
  margin-top: 56px;
  padding-top: 48px;
  border-top: 1px solid var(--pr-line);
}

.member--secretary {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 24px 26px;
}

.member__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--pr-bg);

  .v-icon {
    color: var(--pr-blue) !important;
  }
}

@media (max-width: 1100px) {
  .committee-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .committee-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .secretary {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 40px;
    padding-top: 32px;
  }
}

@media (max-width: 600px) {
  .committee-grid {
    grid-template-columns: 1fr;
  }
}

/* Partneri */
.partners__title {
  margin: 0 0 20px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .16em;
  text-align: center;
  text-transform: uppercase;
  color: var(--pr-muted);

  &:not(:first-child) {
    margin-top: 56px;
  }
}

.logo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
}

.logo-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  padding: 20px 22px;
  border: 1px solid var(--pr-line);
  border-radius: 14px;
  background: #fff;
  transition: border-color .25s ease, box-shadow .25s ease;

  // Logotipi su različitih omjera, pa ih "contain" unutar jednake kutije vizualno ujednačuje
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: grayscale(100%);
    opacity: .75;
    transition: filter .3s ease, opacity .3s ease;
  }

  &:hover {
    border-color: #cfd7e4;
    box-shadow: var(--pr-shadow);

    img {
      filter: none;
      opacity: 1;
    }
  }
}

@media (max-width: 600px) {
  .logo-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .logo-tile {
    height: 92px;
    padding: 14px;
  }
}

/* Kontakt */
.contact__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 24px;
}

.contact__card {
  padding: 40px;
}

.contact__card--social {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  border-radius: var(--pr-radius);
  background: linear-gradient(135deg, #002652 0%, #0a3d86 100%);
  color: #fff;

  .pr-subtitle {
    color: #fff;
  }
}

.contact__list {
  display: grid;
  gap: 18px;
  margin: 28px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    gap: 14px;
    line-height: 1.5;
    color: #2b3445;
  }

  .v-icon {
    color: var(--pr-blue) !important;
  }

  a {
    font-weight: 600;
    text-decoration: none;
  }
}

.contact__label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--pr-muted);
}

.socials {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
}

.social {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, .1);
  text-decoration: none;
  transition: background-color .2s ease, transform .2s ease;

  .v-icon {
    color: #fff !important;
  }

  &:hover {
    background: var(--brand);
    transform: translateY(-2px);
  }
}

@media (max-width: 960px) {
  .contact__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .contact__card {
    padding: 28px 20px;
  }
}

/* Povratak na vrh */
.to-top {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: var(--pr-navy);
  box-shadow: 0 10px 24px rgba(0, 38, 82, .3);
  transition: transform .2s ease;

  &:hover {
    transform: translateY(-2px);
  }
}

.wrap-text {
  white-space: normal;
}
</style>
